import React from 'react';
import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Access from "constants/Access";
import {Button, Badge} from "reactstrap";
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpActiveLocation, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';

const breadcrumbsStatic = [
	{
		name: 'Dashboard',
		url: '/',
		icon: 'dashboard',
	},
	{
		name: 'ERP',
		url: '/erp',
		icon: 'industry',
	},
	{
		name: 'Reports',
		url: '/erp/reports',
		icon: 'file-text-o',
	},
];

class MrpReports extends Framework.Components.View {
	constructor(props) {
		super(props);
		const activeLocation = getMrpActiveLocation(props?.rootState);
		this.state = {
			loading: true,
			error: '',
			days: 30,
			location: activeLocation.location ?? '',
			limit: 250,
			reportType: this.getInitialReportType(),
			searchText: '',
			dateFrom: '',
			dateTo: '',
			reports: {
				financial: [],
				parts_inventory: [],
				guns_built: [],
				labor_time: [],
				scrap: [],
			},
			summary: {},
		};
	}

	componentDidMount() {
		if (super.componentDidMount) {
			super.componentDidMount();
		}
		syncMrpActiveLocation(this.props.rootState);
		this.loadReports();
	}

	componentDidUpdate(prevProps) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			const activeLocation = getMrpActiveLocation(this.props?.rootState);
			this.setState({
				location: activeLocation.location ?? ''
			}, () => {
				this.loadReports();
			});
		}
	}

	getInitialReportType = () => {
		try {
			const query = new URLSearchParams(this.props?.location?.search || '');
			const requested = `${query.get('report') || ''}`.trim().toLowerCase();
			const allowed = ['parts_inventory', 'guns_built', 'labor_time', 'scrap'];
			return allowed.includes(requested) ? requested : 'parts_inventory';
		} catch (e) {
			return 'parts_inventory';
		}
	}

	getAccountParent = () => {
		return this.props?.rootState?.account_number || '9999-9999999';
	}

	getLocations = () => {
		return getMrpActiveLocation(this.props?.rootState).locations;
	}

	formatDate = (value) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return date.toLocaleDateString();
	}

	formatDateTime = (value) => {
		if (!value) return '-';
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;
		return date.toLocaleString();
	}

	formatNumber = (value, maximumFractionDigits = 2) => {
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return '-';
		return numeric.toLocaleString(undefined, {maximumFractionDigits});
	}

	formatCurrency = (value) => {
		const numeric = Number(value);
		if (!Number.isFinite(numeric)) return '-';
		return numeric.toLocaleString(undefined, {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: 2
		});
	}

	formatStatusLabel = (status) => {
		if (!status) return '';
		return `${status}`.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
	}

	loadReports = async () => {
		const {days, location, limit} = this.state;
		this.setState({loading: true, error: ''});
		try {
			const model = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_REPORTS');
			model.parent = this.getAccountParent();
			model.object.days = days;
			model.object.limit = limit;
			applyMrpLocationToRequest(model, this.props.rootState);
			const response = await model.submit(null, 'GET_REPORTS');
			const reports = (response && typeof response === 'object' && response.reports && typeof response.reports === 'object')
				? response.reports
				: {};
			this.setState({
				loading: false,
				reports: {
					financial: Array.isArray(reports.financial) ? reports.financial : [],
					parts_inventory: Array.isArray(reports.parts_inventory) ? reports.parts_inventory : [],
					guns_built: Array.isArray(reports.guns_built) ? reports.guns_built : [],
					labor_time: Array.isArray(reports.labor_time) ? reports.labor_time : [],
					scrap: Array.isArray(reports.scrap) ? reports.scrap : [],
				},
				summary: (response && typeof response.summary === 'object') ? response.summary : {}
			});
		} catch (error) {
			this.setState({
				loading: false,
				error: 'Failed to load reports.'
			});
		}
	}

	getReportDefinitions = () => {
		return {
			parts_inventory: {
				label: 'Parts Inventory',
				dateField: null,
				columns: [
					{label: 'Part', render: (row) => row.part_name || '-', csv: (row) => row.part_name || ''},
					{label: 'SKU', render: (row) => row.part_sku || '-', csv: (row) => row.part_sku || ''},
					{label: 'Location', render: (row) => row.location_name || '-', csv: (row) => row.location_name || ''},
					{label: 'On Hand', render: (row) => this.formatNumber(row.quantity_on_hand, 4), csv: (row) => row.quantity_on_hand || 0},
					{label: 'Avg Cost', render: (row) => this.formatCurrency(row.average_unit_cost), csv: (row) => row.average_unit_cost || 0},
					{label: 'Inventory Value', render: (row) => this.formatCurrency(row.inventory_value), csv: (row) => row.inventory_value || 0},
					{
						label: 'Status',
						render: (row) => Number(row.below_alert) > 0 ? <Badge color="danger">Low</Badge> : <Badge color="success">OK</Badge>,
						csv: (row) => Number(row.below_alert) > 0 ? 'Low' : 'OK'
					},
				],
			},
			guns_built: {
				label: 'Guns Built / Throughput',
				dateField: 'report_date',
				columns: [
					{label: 'Date', render: (row) => this.formatDate(row.report_date), csv: (row) => row.report_date || ''},
					{label: 'Job', render: (row) => row.job_name || `Job ${row.job_id || '-'}`, csv: (row) => row.job_name || row.job_id || ''},
					{label: 'Model', render: (row) => row.model_name || '-', csv: (row) => row.model_name || ''},
					{label: 'Units', render: (row) => this.formatNumber(row.units_reported, 4), csv: (row) => row.units_reported || 0},
					{label: 'Labor Hrs', render: (row) => this.formatNumber(row.labor_hours), csv: (row) => row.labor_hours || 0},
					{label: 'Machine Hrs', render: (row) => this.formatNumber(row.machine_hours), csv: (row) => row.machine_hours || 0},
					{label: 'Units/Labor Hr', render: (row) => this.formatNumber(row.units_per_labor_hour, 4), csv: (row) => row.units_per_labor_hour || 0},
				],
			},
			labor_time: {
				label: 'Time / Labor Report',
				dateField: 'report_date',
				columns: [
					{label: 'Date', render: (row) => this.formatDate(row.report_date), csv: (row) => row.report_date || ''},
					{label: 'Employee', render: (row) => row.employee_name || '-', csv: (row) => row.employee_name || ''},
					{label: 'Type', render: (row) => this.formatStatusLabel(row.entry_type || 'labor'), csv: (row) => row.entry_type || ''},
					{label: 'Job', render: (row) => row.job_name || `Job ${row.job || '-'}`, csv: (row) => row.job_name || row.job || ''},
					{label: 'Station', render: (row) => row.station_name || '-', csv: (row) => row.station_name || ''},
					{label: 'Hours', render: (row) => this.formatNumber(row.total_hours), csv: (row) => row.total_hours || 0},
					{label: 'Entries', render: (row) => this.formatNumber(row.entry_count, 0), csv: (row) => row.entry_count || 0},
				],
			},
			scrap: {
				label: 'Waste / Scrap Report',
				dateField: 'occurred_at',
				columns: [
					{label: 'When', render: (row) => this.formatDateTime(row.occurred_at || row.report_date), csv: (row) => row.occurred_at || row.report_date || ''},
					{label: 'Location', render: (row) => row.location_name || '-', csv: (row) => row.location_name || ''},
					{
						label: 'Item',
						render: (row) => row.part_name || row.model_name || row.product_sku || row.item_type || '-',
						csv: (row) => row.part_name || row.model_name || row.product_sku || row.item_type || ''
					},
					{label: 'Qty', render: (row) => this.formatNumber(row.quantity_scrapped, 4), csv: (row) => row.quantity_scrapped || 0},
					{label: 'Reason', render: (row) => row.reason_name || row.reason_code || '-', csv: (row) => row.reason_name || row.reason_code || ''},
					{label: 'Cost', render: (row) => this.formatCurrency(row.scrap_cost), csv: (row) => row.scrap_cost || 0},
					{label: 'Job', render: (row) => row.job_name || '-', csv: (row) => row.job_name || ''},
					{label: 'Note', render: (row) => row.note || '-', csv: (row) => row.note || ''},
				],
			}
		};
	}

	getActiveDefinition = () => {
		const defs = this.getReportDefinitions();
		return defs[this.state.reportType] || defs.parts_inventory;
	}

	applyClientFilters = (rows, reportType) => {
		const search = `${this.state.searchText || ''}`.trim().toLowerCase();
		const dateFrom = this.state.dateFrom ? new Date(`${this.state.dateFrom}T00:00:00`) : null;
		const dateTo = this.state.dateTo ? new Date(`${this.state.dateTo}T23:59:59.999`) : null;
		const defs = this.getReportDefinitions();
		const dateField = defs[reportType]?.dateField || null;
		return (Array.isArray(rows) ? rows : []).filter((row) => {
			if (search) {
				const haystack = Object.values(row || {}).map((value) => `${value ?? ''}`.toLowerCase()).join(' ');
				if (!haystack.includes(search)) {
					return false;
				}
			}
			if (dateField && (dateFrom || dateTo)) {
				const dateRaw = row?.[dateField] || row?.report_date || null;
				const rowDate = dateRaw ? new Date(dateRaw) : null;
				if (!rowDate || Number.isNaN(rowDate.getTime())) {
					return false;
				}
				if (dateFrom && rowDate < dateFrom) {
					return false;
				}
				if (dateTo && rowDate > dateTo) {
					return false;
				}
			}
			return true;
		});
	}

	getActiveRows = () => {
		const rows = this.state.reports?.[this.state.reportType] || [];
		return this.applyClientFilters(rows, this.state.reportType);
	}

	escapeCsvField = (value) => {
		const text = `${value ?? ''}`;
		if (text.includes('"') || text.includes(',') || text.includes('\n')) {
			return `"${text.replace(/"/g, '""')}"`;
		}
		return text;
	}

	handleExportCsv = () => {
		const definition = this.getActiveDefinition();
		const rows = this.getActiveRows();
		if (!rows.length) {
			return;
		}
		const headers = definition.columns.map((column) => column.label);
		const body = rows.map((row) => definition.columns.map((column) => {
			if (typeof column.csv === 'function') {
				return column.csv(row);
			}
			return row?.[column.key] ?? '';
		}));
		const csvContent = [headers, ...body]
			.map((line) => line.map(this.escapeCsvField).join(','))
			.join('\n');
		const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
		const url = URL.createObjectURL(blob);
		const dateStamp = new Date().toISOString().slice(0, 10);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = `erp-${this.state.reportType}-${dateStamp}.csv`;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		URL.revokeObjectURL(url);
	}

	renderSummaryBar() {
		const {summary, reportType} = this.state;
		if (reportType === 'parts_inventory') {
			return (
				<div className="d-flex flex-wrap mb-3" style={{gap: '8px'}}>
					<span style={styles.summaryPill}><strong>Inventory Value:</strong> {this.formatCurrency(summary?.parts_inventory?.inventory_value_total)}</span>
					<span style={styles.summaryPill}><strong>Low Stock:</strong> {this.formatNumber(summary?.parts_inventory?.below_alert_count, 0)}</span>
				</div>
			);
		}
		if (reportType === 'guns_built') {
			return (
				<div className="d-flex flex-wrap mb-3" style={{gap: '8px'}}>
					<span style={styles.summaryPill}><strong>Units:</strong> {this.formatNumber(summary?.guns_built?.units_reported_total, 4)}</span>
					<span style={styles.summaryPill}><strong>Labor Hrs:</strong> {this.formatNumber(summary?.guns_built?.labor_hours_total)}</span>
					<span style={styles.summaryPill}><strong>Machine Hrs:</strong> {this.formatNumber(summary?.guns_built?.machine_hours_total)}</span>
				</div>
			);
		}
		if (reportType === 'labor_time') {
			return (
				<div className="d-flex flex-wrap mb-3" style={{gap: '8px'}}>
					<span style={styles.summaryPill}><strong>Total Hrs:</strong> {this.formatNumber(summary?.labor_time?.total_hours)}</span>
					<span style={styles.summaryPill}><strong>Labor Hrs:</strong> {this.formatNumber(summary?.labor_time?.labor_hours)}</span>
					<span style={styles.summaryPill}><strong>Machine Hrs:</strong> {this.formatNumber(summary?.labor_time?.machine_hours)}</span>
				</div>
			);
		}
		return (
			<div className="d-flex flex-wrap mb-3" style={{gap: '8px'}}>
				<span style={styles.summaryPill}><strong>Scrap Cost:</strong> {this.formatCurrency(summary?.scrap?.scrap_cost_total)}</span>
				<span style={styles.summaryPill}><strong>Scrap Qty:</strong> {this.formatNumber(summary?.scrap?.quantity_scrapped_total, 4)}</span>
			</div>
		);
	}

	render() {
		const userAccess = this.props?.rootState?.userAccess;
		if (userAccess > Access.Manager) {
			return (
				<div className="container-fluid">
					<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>
					<div className="alert alert-warning">Reports are available to owners/managers only.</div>
				</div>
			);
		}

		const definition = this.getActiveDefinition();
		const rows = this.getActiveRows();
		return (
			<div className="container-fluid">
				<Framework.Components.PageBar breadcrumbs={breadcrumbsStatic} history={this.props.history}/>

				<div style={styles.shell}>
					<div className="d-flex justify-content-between align-items-center mb-3" style={{gap: '8px', flexWrap: 'wrap'}}>
						<h5 style={{margin: 0}}>ERP Reports</h5>
						<div className="d-flex" style={{gap: '8px'}}>
							<Button size="sm" outline color="secondary" onClick={() => this.props.history.push('/erp')}>
								Back to ERP Dashboard
							</Button>
							<Button size="sm" color="primary" onClick={this.loadReports} disabled={this.state.loading}>
								Refresh
							</Button>
						</div>
					</div>

					<div className="row mb-3">
						<div className="col-md-2">
							<label className="small text-muted mb-1 d-block">Report</label>
							<select
								className="form-control form-control-sm"
								value={this.state.reportType}
								onChange={(event) => this.setState({reportType: event.target.value})}
							>
								<option value="parts_inventory">Parts Inventory</option>
								<option value="guns_built">Guns Built</option>
								<option value="labor_time">Time / Labor</option>
								<option value="scrap">Waste / Scrap</option>
							</select>
						</div>
						<div className="col-md-2">
							<label className="small text-muted mb-1 d-block">Window</label>
							<select
								className="form-control form-control-sm"
								value={this.state.days}
								onChange={(event) => this.setState({days: Number(event.target.value)})}
							>
								<option value={7}>7 days</option>
								<option value={30}>30 days</option>
								<option value={90}>90 days</option>
								<option value={180}>180 days</option>
							</select>
						</div>
							<div className="col-md-2">
								<MrpLocationControl
									rootState={this.props.rootState}
									id="mrp-reports-location"
									label="Location"
									note={LOCATION_NOTE}
								/>
							</div>
						<div className="col-md-2">
							<label className="small text-muted mb-1 d-block">From</label>
							<input
								type="date"
								className="form-control form-control-sm"
								value={this.state.dateFrom}
								onChange={(event) => this.setState({dateFrom: event.target.value})}
							/>
						</div>
						<div className="col-md-2">
							<label className="small text-muted mb-1 d-block">To</label>
							<input
								type="date"
								className="form-control form-control-sm"
								value={this.state.dateTo}
								onChange={(event) => this.setState({dateTo: event.target.value})}
							/>
						</div>
						<div className="col-md-2">
							<label className="small text-muted mb-1 d-block">Search</label>
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Search rows..."
								value={this.state.searchText}
								onChange={(event) => this.setState({searchText: event.target.value})}
							/>
						</div>
						</div>
						<div className="small text-muted mb-3">
							Window controls how much report data is fetched from the server. From and To only filter the rows already loaded on this page.
						</div>

						<div className="d-flex justify-content-between align-items-center mb-2" style={{gap: '8px', flexWrap: 'wrap'}}>
						<h6 style={{margin: 0}}>{definition.label}</h6>
						<div className="d-flex align-items-center" style={{gap: '8px'}}>
							<span className="small text-muted">{rows.length} rows</span>
							<Button size="sm" color="primary" onClick={this.handleExportCsv} disabled={rows.length === 0}>
								Export CSV
							</Button>
						</div>
					</div>

					{this.renderSummaryBar()}

					{this.state.error && (
						<div className="alert alert-warning">{this.state.error}</div>
					)}
					{this.state.loading ? (
						<Framework.Elements.LoadingSpinner isLoading={true}/>
					) : (
						<div className="table-responsive">
							<table className="table table-sm table-bordered mb-0">
								<thead>
								<tr>
									{definition.columns.map((column) => (
										<th key={column.label}>{column.label}</th>
									))}
								</tr>
								</thead>
								<tbody>
								{rows.map((row, rowIndex) => (
									<tr key={row?.pkey || row?.job_id || row?.part_id || `row-${rowIndex}`}>
										{definition.columns.map((column, colIndex) => (
											<td key={`${column.label}-${colIndex}`}>{column.render(row, rowIndex)}</td>
										))}
									</tr>
								))}
								</tbody>
							</table>
							{rows.length === 0 && <div className="text-muted mt-3">No rows match current filters.</div>}
						</div>
					)}
				</div>
			</div>
		);
	}
}

const styles = {
	shell: {
		background: '#ffffff',
		border: '1px solid #d6e1ef',
		borderRadius: '12px',
		padding: '16px',
		boxShadow: '0 8px 18px rgba(15, 56, 99, 0.06)',
	},
	summaryPill: {
		padding: '4px 10px',
		borderRadius: '999px',
		border: '1px solid #d9e7f7',
		background: '#eff6ff',
		color: '#294a67',
		fontSize: '12px',
	}
};

export default Framework.Components.withRootState(MrpReports);
